import { useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";

interface ScrollPositionManager {
  saveScrollPosition: (key?: string) => void;
  restoreScrollPosition: (key?: string) => void;
  resetScrollPosition: (key?: string) => void;
  scrollToProduct: (productIndex: number, virtualizer?: any) => void;
}

export const useScrollPosition = (
  containerRef: React.RefObject<HTMLDivElement | null>,
  virtualizerRef?: React.RefObject<any>
): ScrollPositionManager => {
  const location = useLocation();
  const scrollPositions = useRef<Map<string, number>>(new Map());

  // Generate a unique key for the current route
  const getRouteKey = useCallback(
    (customKey?: string) => {
      if (customKey) return customKey;
      return `${location.pathname}${location.search}`;
    },
    [location]
  );

  // Save current scroll position
  const saveScrollPosition = useCallback(
    (key?: string) => {
      if (!containerRef.current) return;

      const routeKey = getRouteKey(key);
      const scrollTop = containerRef.current.scrollTop;
      scrollPositions.current.set(routeKey, scrollTop);

      // Also store in sessionStorage for persistence across page reloads
      sessionStorage.setItem(`scroll-${routeKey}`, scrollTop.toString());
    },
    [containerRef, getRouteKey]
  );

  // Restore scroll position
  const restoreScrollPosition = useCallback(
    (key?: string) => {
      if (!containerRef.current) return;

      const routeKey = getRouteKey(key);

      // Try to get from memory first, then sessionStorage
      let scrollTop = scrollPositions.current.get(routeKey);
      if (scrollTop === undefined) {
        const stored = sessionStorage.getItem(`scroll-${routeKey}`);
        scrollTop = stored ? parseInt(stored, 10) : 0;
      }

      if (scrollTop > 0) {
        // Use setTimeout to ensure DOM is ready
        setTimeout(() => {
          if (containerRef.current) {
            containerRef.current.scrollTo({
              top: scrollTop,
              behavior: "auto", // Use 'auto' for immediate positioning, 'smooth' for animated
            });
          }
        }, 0);
      }
    },
    [containerRef, getRouteKey]
  );

  // Reset both the rendered list and its saved route position. Clearing the
  // saved value prevents a later restore from undoing the reset while data is
  // loading or the list container is being remounted.
  const resetScrollPosition = useCallback(
    (key?: string) => {
      const routeKey = getRouteKey(key);
      scrollPositions.current.set(routeKey, 0);
      sessionStorage.setItem(`scroll-${routeKey}`, "0");

      if (containerRef.current) {
        containerRef.current.scrollTop = 0;
      }

      virtualizerRef?.current?.scrollToIndex?.(0, {
        align: "start",
        behavior: "auto",
      });
    },
    [containerRef, getRouteKey, virtualizerRef]
  );

  // Scroll to a specific product by index using virtualizer
  const scrollToProduct = useCallback(
    (productIndex: number, virtualizer?: any) => {
      if (!containerRef.current) return;

      if (virtualizer && virtualizerRef?.current) {
        // Use virtualizer to scroll to specific item
        virtualizerRef.current.scrollToIndex(productIndex, {
          align: "start",
          behavior: "auto",
        });
      } else {
        // Fallback: estimate position (assuming ~130px per item as in ProductsPage)
        const estimatedPosition = productIndex * 130;
        containerRef.current.scrollTo({
          top: estimatedPosition,
          behavior: "auto",
        });
      }
    },
    [containerRef, virtualizerRef]
  );

  // Clean up old scroll positions to prevent memory leaks
  useEffect(() => {
    const cleanup = () => {
      // Keep only the last 10 scroll positions
      if (scrollPositions.current.size > 10) {
        const entries = Array.from(scrollPositions.current.entries());
        const toKeep = entries.slice(-10);
        scrollPositions.current.clear();
        toKeep.forEach(([key, value]) => {
          scrollPositions.current.set(key, value);
        });
      }
    };

    cleanup();
  }, [location]);

  return {
    saveScrollPosition,
    restoreScrollPosition,
    resetScrollPosition,
    scrollToProduct,
  };
};
